import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Button,
  Typography,
  Box,
  Card,
  CardMedia,
  IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import FileUpload from 'react-mui-fileuploader';
import { ExtendedFileProps } from 'react-mui-fileuploader/dist/types/index.types';

import apiClient from 'api';
import Loader from 'components/loader';

import { getEnvironment } from 'config/environments/environment';

import { IMedia, IMediaData, MediaSlotName } from 'types/types';
import logoImage from '../../assets/img/logoRound.svg';

interface IProps {
  id: number;
  slot?: MediaSlotName;
  title?: string;
  notifyUser(
    message: string,
    status: 'error' | 'success' | 'info' | 'warning'
  ): void;
}

const emptyMedia: IMedia = { id: 0, path: '', sort: null };

const InfoSectionMedia: FC<IProps> = ({
  id,
  slot = 'main',
  title,
  notifyUser
}) => {
  const [media, setMedia] = useState<IMedia>(emptyMedia);
  const [loading, setLoading] = useState<boolean>(true);
  const [coverToUpload, setCoverToUpload] = useState<Blob | null>(null);

  const fetchMedia = useCallback(() => {
    if (!id) return;

    setLoading(true);

    apiClient.media
      .getMedia('categories', id)
      .then((resp) => {
        if (resp.status === 200) {
          // resp.data = { main: [...], gallery: [...], icon: [...], extra1: [...], ... }
          const data = resp.data as IMediaData;
          const arr = data?.[slot] ?? [];
          setMedia(arr[0] ? { ...arr[0] } : emptyMedia);
        } else {
          notifyUser(`Ошибка ${resp.status}: ${resp.statusText}`, 'error');
        }
      })
      .catch((e) => {
        notifyUser(e?.message || 'Ошибка загрузки медиа', 'error');
      })
      .finally(() => setLoading(false));
  }, [id, slot, notifyUser]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const uploadCover = useCallback(() => {
    if (!coverToUpload) return;

    const formData = new FormData();
    formData.append('files[]', coverToUpload);

    setLoading(true);

    apiClient.media
      .postMedia(formData)
      .then(async (mediaResp) => {
        if (mediaResp.status !== 200 || !mediaResp.data?.[0]) {
          notifyUser('Ошибка загрузки файла', 'error');
          return;
        }

        const uploaded: IMedia = mediaResp.data[0];

        const replaceResp = await apiClient.media.replaceMedia(
          'categories',
          id,
          slot,
          uploaded.id
        );

        if (replaceResp.status === 200) {
          setCoverToUpload(null);
          fetchMedia();
          notifyUser('Изображение обновлено', 'success');
        } else {
          notifyUser(
            `Ошибка ${replaceResp.status}: ${replaceResp.statusText}`,
            'error'
          );
        }
      })
      .catch((e) => {
        notifyUser(e?.message || 'Ошибка загрузки', 'error');
      })
      .finally(() => setLoading(false));
  }, [coverToUpload, id, slot, fetchMedia, notifyUser]);

  const deleteImage = useCallback(() => {
    if (!media.id) return;

    setLoading(true);

    apiClient.media
      .deleteMedia(media.id)
      .then((resp) => {
        if (resp.status === 200) {
          setMedia(emptyMedia);
          notifyUser('Изображение удалено', 'success');
          // на всякий случай перечитываем слот
          fetchMedia();
        } else {
          notifyUser(`Ошибка ${resp.status}: ${resp.statusText}`, 'error');
        }
      })
      .catch((e) => {
        notifyUser(e?.message || 'Ошибка удаления', 'error');
      })
      .finally(() => setLoading(false));
  }, [media.id, fetchMedia, notifyUser]);

  const printCover = () => {
    const fileUrl = media.path
      ? `${getEnvironment().baseUrl}/files/public/${media.path}`
      : '';
    return media.path ? (
      <Card sx={{ position: 'relative', height: 200, width: 200 }}>
        <CardMedia
          component="img"
          sx={{ height: 200, width: 200 }}
          image={fileUrl}
          alt={title ?? 'Обложка'}
        />
        <IconButton
          sx={{ position: 'absolute', top: '10px', right: '10px' }}
          onClick={deleteImage}
        >
          <DeleteOutlineIcon color="error" />
        </IconButton>
      </Card>
    ) : (
      <Box>
        <FileUpload
          onFilesChange={(files: ExtendedFileProps[]) => {
            setCoverToUpload(files[0] || null);
          }}
          title="Загрузка обложки"
          header="Переместите"
          leftLabel="или"
          rightLabel="чтобы загрузить"
          buttonLabel="НАЖМИТЕ"
          buttonRemoveLabel="Удалить выбранное"
          maxFileSize={21}
          maxUploadFiles={1}
          maxFilesContainerHeight={400}
          acceptedType={'image/*'}
          errorSizeMessage="Недопустимый размер изображений"
          allowedExtensions={[
            'jpg',
            'jpeg',
            'png',
            'gif',
            'webp',
            'svg',
            'svg+xml'
          ]}
          onError={(e) => {
            notifyUser(e, 'error');
          }}
          imageSrc={logoImage}
          BannerProps={{ elevation: 0, variant: 'outlined' }}
          showPlaceholderImage={false}
          PlaceholderGridProps={{ md: 4 }}
          LabelsGridProps={{ md: 8 }}
          ContainerProps={{
            elevation: 0,
            variant: 'outlined',
            sx: { p: 2 }
          }}
          PlaceholderImageDimension={{
            xs: { width: 512, height: 512 },
            sm: { width: 512, height: 512 },
            md: { width: 512, height: 512 },
            lg: { width: 512, height: 512 }
          }}
        />

        <Button onClick={uploadCover} disabled={!coverToUpload}>
          Отправить
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ alignSelf: 'flex-start', px: 5, width: '100%' }}>
      <Typography variant="h6" sx={{ pb: 3 }}>
        {title ?? 'Обложка'}
      </Typography>

      <Box sx={{ maxWidth: '250px' }}>
        {!loading ? printCover() : <Loader />}
      </Box>
    </Box>
  );
};

export default InfoSectionMedia;
